import { useMemo, useState } from "react";
import { Headphones, Heart, Music4, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, Label, MonoMeta } from "@/design/primitives";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import bakedBoard from "@/data/musicBoard.json";
import { useMusicPlayer } from "@/music/MusicPlayer";
import {
  buildPlaylists,
  catalogDates,
  dateLabel,
  formatDuration,
  formatTotalDuration,
  groupTracksByDate,
  isBed,
  latestDate,
  normalizeItems,
  todayIso as libTodayIso,
} from "@/music/library";
import { favoriteValue, favoritesPlaylist, isFavorited } from "@/music/favorites";

// ── The music library (Phase 2) ───────────────────────────────────────────
//
// The Spotify pattern, in the AgenticOS voice: a left rail of playlists, a
// right list of tracks. What makes it this OS: the catalog is a BOARD, not a
// playlist of favorites. The daily set is rendered by Kit's pipeline, baked to
// musicBoard.json, and pushed live to /api/board/music.
//
// The floor: the page renders the baked catalog whenever the live board is
// unreachable. The catalog is never empty and never wrong about which day it
// is — it just says so.

const stampText = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const HeadphoneBadge = () => (
  <span
    data-testid="music-headphones-badge"
    title="Headphones required"
    className="inline-flex shrink-0 items-center gap-1 rounded-chip border border-os-bronze border-b-os-bronze-strong bg-os-gold-tint px-2 py-0.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-2"
  >
    <Headphones className="h-3 w-3" strokeWidth={1.75} />
    Headphones
  </span>
);

// One track row. The play button is the only affordance that touches the
// player; the row itself never hides a state. The heart is Jeff's overlay:
// clicking it favorites the track (persisted through the board's /api/action
// overlay when live, localStorage when offline — favorites.js owns the shape).
const HeartButton = ({ track, onToggle, pending }) => {
  const fav = isFavorited(track);
  return (
    <button
      type="button"
      aria-pressed={fav}
      aria-label={fav ? `Remove ${track.title} from favorites` : `Add ${track.title} to favorites`}
      data-testid={`music-fav-${track.slot_id}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(track);
      }}
      className={cn(
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
        fav ? "text-os-bronze" : "text-os-ink-4 hover:text-os-ink-2",
        pending && "opacity-50",
      )}
    >
      <Heart className="h-3.5 w-3.5" strokeWidth={1.75} fill={fav ? "currentColor" : "none"} />
    </button>
  );
};

const TrackRow = ({ track, onPlay, playing, isCurrent, queue, onToggleFavorite, favPending }) => {
  return (
    <li
      data-testid={`music-track-${track.slot_id}`}
      className={cn(
        "flex items-center gap-3 rounded-control px-3 py-2",
        isCurrent ? "bg-os-wash" : "hover:bg-os-wash",
      )}
    >
      <button
        type="button"
        aria-label={`Play ${track.title}`}
        data-testid={`music-play-${track.slot_id}`}
        onClick={() => onPlay(track, queue)}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-os-border text-os-ink hover:border-os-ink-3"
      >
        {playing && isCurrent ? (
          <span className="flex h-3 items-end gap-[2px]" aria-hidden="true">
            <span className="h-2 w-[2px] bg-os-ink" />
            <span className="h-3 w-[2px] bg-os-ink" />
            <span className="h-1.5 w-[2px] bg-os-ink" />
          </span>
        ) : (
          <Play className="h-3.5 w-3.5" strokeWidth={1.75} />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[0.875rem] font-medium text-os-ink">
            {track.title}
          </span>
          {track.headphones_required && <HeadphoneBadge />}
        </div>
        <MonoMeta className="mt-0.5 block truncate">
          {track.artist}
          {track.genre ? ` · ${track.genre}` : ""}
          {track.beat_hz ? ` · ${track.beat_hz} Hz` : ""}
        </MonoMeta>
      </div>

      <span className="shrink-0 font-mono text-[0.6875rem] tabular-nums text-os-ink-3">
        {formatDuration(track.duration_s)}
      </span>

      <HeartButton track={track} onToggle={onToggleFavorite} pending={favPending} />
    </li>
  );
};

// A playlist in the left rail: name, how many tracks, how long.
const PlaylistTile = ({ playlist, active, onSelect }) => (
  <button
    type="button"
    data-testid={`music-playlist-${playlist.key}`}
    onClick={onSelect}
    aria-current={active ? "true" : undefined}
    className={cn(
      "w-full rounded-control px-3 py-2.5 text-left transition-colors",
      active ? "bg-os-wash" : "hover:bg-os-wash",
    )}
  >
    <div className="flex items-center gap-2">
      <Music4
        className={cn("h-3.5 w-3.5 shrink-0", active ? "text-os-ink" : "text-os-ink-4")}
        strokeWidth={1.75}
      />
      <span
        className={cn(
          "truncate text-[0.875rem]",
          active ? "font-semibold text-os-ink" : "font-medium text-os-ink-2",
        )}
      >
        {playlist.label}
      </span>
    </div>
    <MonoMeta className="mt-1 block pl-5.5 pl-[1.375rem]">
      {playlist.tracks.length} tracks · {formatTotalDuration(playlist.tracks)}
    </MonoMeta>
  </button>
);

export default function MusicPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const board = useBoard("music");
  const player = useMusicPlayer();
  const [selected, setSelected] = useState("today");

  // The REAL current date in the portfolio's home zone — through the library's
  // seam so tests can pin "today". Computed once per mount; the midnight
  // boundary re-picking "today" on a long-lived visit is the catalog's next
  // push's problem, not the list's.
  const todayIso = useMemo(() => libTodayIso(), []);

  // The floor: the baked catalog, always available. The live board wins when
  // it answers.
  const liveTracks = useMemo(
    () => (board.live ? normalizeItems(board.items) : []),
    [board.live, board.items],
  );
  const bakedTracks = useMemo(() => normalizeItems(bakedBoard.items), []);
  const tracks = liveTracks.length ? liveTracks : bakedTracks;
  const live = liveTracks.length > 0;

  // ── favorites state ──────────────────────────────────────────────────────
  // Jeff's hearts ride the track overlay (payload.jeff.favorited). Live: the
  // toggle goes through /api/action (favorite/unfavorite) and the overlay
  // merge lands in local state like every board action. Offline (baked floor):
  // the heart still works — it persists in localStorage keyed by slug, and the
  // server ingest carries the same overlay shape once the board is live again,
  // so the two paths write the SAME record shape.
  const [offlineHearts, setOfflineHearts] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem("music.favorites") || "{}");
    } catch {
      return {};
    }
  });
  const [favPending, setFavPending] = useState(null);

  const tracksWithHearts = useMemo(
    () =>
      tracks.map((t) => {
        const off = offlineHearts[t.slug] || offlineHearts[t.slot_id];
        if (!off) return t;
        return { ...t, jeff: { ...(t.jeff || {}), favorited: off } };
      }),
    [tracks, offlineHearts],
  );

  const toggleFavorite = async (track) => {
    // Identity is the SLOT id (favorites.js) — a heart belongs to the song,
    // not to one date's render. The live API target is the board slug (the
    // row shape /api/action addresses); the persistent store is the slot.
    const slot = track.slot_id || track.id;
    if (!slot) return;
    setFavPending(slot);
    const overlayValue = favoriteValue(track);
    if (live) {
      try {
        await board.act(overlayValue ? "favorite" : "unfavorite", track.slug);
        // Keep the offline store in step with the live overlay, so the hearts
        // survive a later offline visit AND the server ingest carries the
        // same shape back on the next catalog push.
        try {
          const store = JSON.parse(window.localStorage.getItem("music.favorites") || "{}");
          if (overlayValue) store[slot] = overlayValue.favorited;
          else delete store[slot];
          window.localStorage.setItem("music.favorites", JSON.stringify(store));
          setOfflineHearts(store);
        } catch { /* private mode: hearts stay live-only */ }
      } catch {
        board.setNotice({ tone: "amber", text: "That heart didn't save — try again." });
      } finally {
        setFavPending(null);
      }
    } else {
      const store = { ...offlineHearts };
      if (overlayValue) store[slot] = overlayValue.favorited;
      else delete store[slot];
      try {
        window.localStorage.setItem("music.favorites", JSON.stringify(store));
      } catch { /* private mode: hearts stay for this visit only */ }
      setOfflineHearts(store);
      setFavPending(null);
    }
  };

  const playlists = useMemo(() => {
    const base = buildPlaylists(tracksWithHearts, todayIso);
    return [...base, favoritesPlaylist(tracksWithHearts)];
  }, [tracksWithHearts, todayIso]);
  const playlist = useMemo(
    () => playlists.find((p) => p.key === selected) || playlists[0],
    [playlists, selected],
  );

  const meta = board.live && board.meta ? board.meta : bakedBoard.meta;
  const date = meta?.date || tracks[0]?.date || "";
  const theme = meta?.theme || {};
  const totals = meta?.totals || {};
  // Today-first is a HARD contract: when the newest set in the library is not
  // the real current day, the header says which day it IS and why, instead of
  // letting old tracks pose as today's music.
  const newestDate = latestDate(tracks);
  const daysInCatalog = catalogDates(tracks).length;
  const dayLag = newestDate && todayIso && newestDate !== todayIso;

  // Per-track play: the track plays within the playlist it was clicked in, so
  // "next" walks that playlist.
  const onPlay = (track, queue) => {
    player.playTrack(track, queue);
  };

  const onPlayAll = () => {
    if (!playlist || !playlist.tracks.length) return;
    player.playPlaylist(playlist.tracks, 0);
  };

  const isCurrent = (t) => player.currentTrack?.id === t.id;

  return (
    <div data-testid="music-page" className="w-full font-inter text-os-ink">
      <div
        className={
          desktop
            ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6"
            : "flex flex-col gap-3 px-4 pb-8 pt-4"
        }
      >
        {/* ── header ── */}
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[1.5rem] font-semibold tracking-[-0.01em]">Music</h1>
            <p className="mt-1 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
              {theme.label ? `${theme.label} · ` : ""}
              {date}
              {totals.tracks ? ` · ${totals.tracks} tracks` : ""}
              {daysInCatalog > 1 ? ` · ${daysInCatalog} days` : ""}
              {totals.durationS
                ? ` · ${formatTotalDuration(tracks)}`
                : ""}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {meta?.pushedAt && (
              <MonoMeta data-testid="music-stamp">
                {live ? "live" : "offline copy"} · {stampText(meta.pushedAt)}
              </MonoMeta>
            )}
            <button
              type="button"
              data-testid="music-play-all"
              onClick={onPlayAll}
              disabled={!playlist || !playlist.tracks.length}
              className={cn(
                "inline-flex items-center gap-2 rounded-control border border-os-border px-3 py-2 text-[0.8125rem] font-medium",
                playlist && playlist.tracks.length
                  ? "text-os-ink hover:border-os-ink-3 hover:bg-os-wash"
                  : "cursor-not-allowed text-os-ink-4",
              )}
            >
              <Play className="h-3.5 w-3.5" strokeWidth={1.75} />
              Play {playlist ? playlist.label : "playlist"}
            </button>
          </div>
        </header>

        {/* ── the offline notice: the catalog is committed, the board is not ── */}
        {!live && (
          <Card quiet className="px-5 py-3">
            <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
              Kit&rsquo;s music feed is offline this visit — the library shows
              the last committed day.
            </p>
          </Card>
        )}

        {/* ── the day-source notice: the newest set is NOT the current day ── */}
        {dayLag && (
          <Card quiet className="px-5 py-3" data-testid="music-day-lag">
            <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
              Today&rsquo;s set hasn&rsquo;t been rendered yet — the library
              leads with {dateLabel(newestDate, todayIso)}&rsquo;s mix until the
              new tracks land.
            </p>
          </Card>
        )}

        {/* ── the library ── */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
          {/* the rail: the three playlists */}
          <Card quiet className="flex flex-col gap-1 p-2">
            <Label className="px-3 py-2">Playlists</Label>
            {playlists.map((p) => (
              <PlaylistTile
                key={p.key}
                playlist={p}
                active={p.key === playlist?.key}
                onSelect={() => setSelected(p.key)}
              />
            ))}
          </Card>

          {/* the tracks */}
          <Card quiet className="flex min-h-0 flex-col">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-os-border px-4 py-3">
              <div>
                <h2 className="text-[0.9375rem] font-semibold tracking-[-0.01em]">
                  {playlist?.label}
                </h2>
                <p className="mt-0.5 text-[0.8125rem] text-os-ink-3">
                  {playlist?.description}
                </p>
              </div>
              <MonoMeta>
                {playlist?.tracks.length || 0} tracks ·{" "}
                {formatTotalDuration(playlist?.tracks || [])}
              </MonoMeta>
            </div>

            {playlist?.tracks.length ? (
              <ul className="flex flex-col gap-0.5 p-2" data-testid="music-track-list">
                {groupTracksByDate(playlist.tracks).map((section) => {
                  const isTodaySection = section.date && section.date === todayIso;
                  return (
                    <li key={section.date || "__undated__"} className="mt-1 first:mt-0">
                      {section.date && (
                        <div
                          data-testid={`music-day-${section.date}`}
                          className="flex items-center gap-2 pb-1 pl-3 pt-1.5"
                        >
                          <MonoMeta
                            className={
                              isTodaySection ? "text-os-bronze" : "text-os-ink-4"
                            }
                          >
                            {dateLabel(section.date, todayIso)}
                          </MonoMeta>
                          {isTodaySection && (
                            <span
                              data-testid="music-today-chip"
                              className="inline-flex items-center rounded-chip border border-os-bronze border-b-os-bronze-strong bg-os-gold-tint px-2 py-0.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-bronze"
                            >
                              New today
                            </span>
                          )}
                        </div>
                      )}
                      <ul className="flex flex-col gap-0.5">
                        {section.tracks.map((t) => (
                          <TrackRow
                            key={t.id}
                            track={t}
                            queue={playlist.tracks}
                            onPlay={onPlay}
                            playing={player.playing}
                            isCurrent={isCurrent(t)}
                            onToggleFavorite={toggleFavorite}
                            favPending={favPending === (t.slot_id || t.id)}
                          />
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div
                className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-12 text-center"
                data-testid="music-empty"
              >
                <Music4 className="h-5 w-5 text-os-ink-4" strokeWidth={1.5} />
                <p className="text-[0.875rem] text-os-ink-3">
                  No tracks in this playlist yet.
                </p>
                <p className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
                  {playlist?.key === "favorites"
                    ? "heart a track and it lives here"
                    : "the daily set is rendered overnight"}
                </p>
              </div>
            )}

            {/* the headphone note for a bed playlist */}
            {playlist?.headphones && (
              <div
                data-testid="music-headphones-note"
                className="flex items-center gap-2 border-t border-os-border px-4 py-3"
              >
                <Headphones className="h-3.5 w-3.5 text-os-ink-3" strokeWidth={1.75} />
                <p className="text-[0.8125rem] text-os-ink-2">
                  These beds are binaural — the beat only lands with headphones on.
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* the local-only note: the catalog knows the track, the file is not here */}
        {player.isLocalOnly && player.currentTrack && (
          <Card quiet className="px-5 py-3" data-testid="music-local-only">
            <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
              {player.currentTrack.title} is in the catalog, but its audio lives on
              Kit&rsquo;s machine — this browser cannot reach it yet.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
